import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAdminDashboard, getCustomerDashboard } from '../api/dashboard';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { MetricCard } from '../components/dashboard/MetricCard';
import { MonthlyTicketsChart } from '../components/dashboard/MonthlyTicketsChart';
import { CustomerTicketsTable } from '../components/dashboard/CustomerTicketsTable';
import { RecentTickets } from '../components/dashboard/RecentTickets';
import { Ticket, CheckCircle, Clock } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';

// Presets including 1 week, 2 weeks, and custom date ranges
const TICKET_VOLUME_PRESETS = {
  '1w': { api: { days: 7 }, label: 'Last 1 week', mode: 'days' },
  '2w': { api: { days: 14 }, label: 'Last 2 weeks', mode: 'days' },
  '3': { api: { months: 3 }, label: 'Last 3 months', mode: 'rolling' },
  '6': { api: { months: 6 }, label: 'Last 6 months', mode: 'rolling' },
  '12': { api: { months: 12 }, label: 'Last 12 months', mode: 'rolling' },
  ytd: { api: { range: 'ytd' }, label: 'Year to date', mode: 'ytd' },
  custom: { api: { range: 'custom' }, label: 'Custom Range', mode: 'custom' },
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Returns "YYYY-MM-DD" for a Date object using LOCAL date parts (no UTC shift)
const formatDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Returns a Date at midnight (00:00:00) in local time for a given "YYYY-MM-DD" string.
const localMidnight = (dateStr) => {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Date(y, mo - 1, d, 0, 0, 0, 0);
};

// Generates dynamic timeline buckets (days or months) based on current preset selections
function buildDataSlots(presetKey, customStartDate, customEndDate) {
  const cfg = TICKET_VOLUME_PRESETS[presetKey] || TICKET_VOLUME_PRESETS['12'];
  const slots = [];

  let isDayMode = cfg.mode === 'days';
  const todayMidnight = localMidnight(formatDateString(new Date()));

  let startDate = new Date(todayMidnight);
  let endDate   = new Date(todayMidnight);

  if (cfg.mode === 'days') {
    // Go back (days - 1) so the range is: [today - (days-1)]  …  today  (inclusive)
    startDate.setDate(todayMidnight.getDate() - (cfg.api.days - 1));
  } else if (cfg.mode === 'custom' && customStartDate && customEndDate) {
    startDate = localMidnight(customStartDate);
    endDate   = localMidnight(customEndDate);
    const diffDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (diffDays <= 31 || customStartDate === customEndDate) {
      isDayMode = true;
    }
  }

  // FIXED: isDayMode must execute first before moving to standard custom blocks
  if (isDayMode) {
    const current = new Date(startDate);
    while (current <= endDate) {
      const dayStr = formatDateString(current);
      const displayLabel = `${current.getDate()} ${MONTH_NAMES[current.getMonth()]}`;
      slots.push({ key: dayStr, label: displayLabel, type: 'day' });
      current.setDate(current.getDate() + 1);
    }
    return slots;
  }

  // Year to Date processing configuration
  if (cfg.mode === 'ytd') {
    const y = todayMidnight.getFullYear();
    const end = todayMidnight.getMonth();
    for (let m = 0; m <= end; m += 1) {
      slots.push({
        key: `${y}-${String(m + 1).padStart(2, '0')}`,
        label: `${MONTH_NAMES[m]} '${String(y).slice(-2)}`,
        type: 'month',
      });
    }
    return slots;
  }

  // Extended Custom Range (Month-by-month processing configuration)
  if (cfg.mode === 'custom' && customStartDate && customEndDate) {
    const start = localMidnight(customStartDate);
    const end   = localMidnight(customEndDate);
    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    while (current <= end) {
      const y  = current.getFullYear();
      const mo = current.getMonth() + 1;
      slots.push({
        key: `${y}-${String(mo).padStart(2, '0')}`,
        label: `${MONTH_NAMES[current.getMonth()]} '${String(y).slice(-2)}`,
        type: 'month',
      });
      current.setMonth(current.getMonth() + 1);
    }
    return slots;
  }

  // Standard Rolling Month presets configuration (3, 6, 12 months)
  const n = cfg.api.months || 12;
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth() - i, 1);
    const y  = d.getFullYear();
    const mo = d.getMonth() + 1;
    slots.push({
      key: `${y}-${String(mo).padStart(2, '0')}`,
      label: `${MONTH_NAMES[d.getMonth()]} '${String(y).slice(-2)}`,
      type: 'month',
    });
  }
  return slots;
}

function processMonthlyDataFromAPI(apiData, presetKey, customStart, customEnd) {
  const slots = buildDataSlots(presetKey, customStart, customEnd);
  if (slots.length === 0) return [];
  
  const isDayMode = slots[0].type === 'day';
  const countsByKey = {};

  const pickDateKey = (item) => {
    const raw =
      item?.period_ym ??
      item?.PERIOD_YM ??
      item?.year_month ??
      item?.YEAR_MONTH ??
      item?.yearMonth ??
      item?.created_at ??
      item?.date;
    if (raw == null || raw === '') return '';

    const s = String(raw).trim();

    if (isDayMode) {
      const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1]; // e.g. "2026-05-25" — exact, no timezone shift
    } else {
      const m = s.match(/^(\d{4})-(\d{2})/);
      if (m) return `${m[1]}-${m[2]}`; // e.g. "2026-05"
    }

    // Fallback: parse and format using local date parts
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
      return isDayMode
        ? formatDateString(date)
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }
    return '';
  };

  const pickTicketCount = (item) => {
    const v = item?.ticket_count ?? item?.count ?? item?.cnt ?? item?.COUNT ?? item?.tickets;
    if (typeof v === 'bigint') return Number(v);
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  };

  if (Array.isArray(apiData)) {
    apiData.forEach((item) => {
      if (!item) return;
      const key = pickDateKey(item);
      if (!key) return;
      const c = pickTicketCount(item);
      countsByKey[key] = (countsByKey[key] || 0) + c;
    });
  }

  return slots.map((s) => ({
    month: s.label,
    tickets: countsByKey[s.key] || 0,
  }));
}

function generateMonthlyData(recentTickets, presetKey, customStart, customEnd) {
  const slots = buildDataSlots(presetKey, customStart, customEnd);
  if (slots.length === 0) return [];
  
  const isDayMode = slots[0].type === 'day';
  const countsByKey = {};

  if (Array.isArray(recentTickets)) {
    recentTickets.forEach((ticket) => {
      const rawDate = ticket?.created_at || ticket?.updated_at;
      if (rawDate) {
        try {
          const s = String(rawDate).trim();
          if (isDayMode) {
            const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
            if (m) {
              countsByKey[m[1]] = (countsByKey[m[1]] || 0) + 1;
              return;
            }
          }
          const date = new Date(rawDate);
          if (!Number.isNaN(date.getTime())) {
            const key = isDayMode
              ? formatDateString(date)
              : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            countsByKey[key] = (countsByKey[key] || 0) + 1;
          }
        } catch (error) {
          console.warn('Invalid date in ticket:', rawDate);
        }
      }
    });
  }

  return slots.map((s) => ({
    month: s.label,
    tickets: countsByKey[s.key] || 0,
  }));
}

const Dashboard = () => {
  const { user } = useAuth();
  const normalizedRole = String(user?.role || '').toLowerCase();
  const isAdminScopeRole =
    normalizedRole === 'admin' ||
    normalizedRole === 'employee' ||
    normalizedRole === 'global_support' ||
    normalizedRole === 'super_admin';
  const navigate = useNavigate();
  
  const [dashboardData, setDashboardData]   = useState(null);
  const [loading, setLoading]               = useState(true);
  const [monthlyData, setMonthlyData]       = useState([]);
  const [customerData, setCustomerData]     = useState([]);
  
  const [ticketVolumePreset, setTicketVolumePreset] = useState(() => {
    try {
      const saved = sessionStorage.getItem('dashboardTicketVolume');
      if (saved && TICKET_VOLUME_PRESETS[saved]) return saved;
    } catch (_) { /* ignore */ }
    return '12';
  });

  const [customStartDate, setCustomStartDate] = useState(() => {
    return sessionStorage.getItem('dashboardCustomStart') || '';
  });
  const [customEndDate, setCustomEndDate] = useState(() => {
    return sessionStorage.getItem('dashboardCustomEnd') || '';
  });

  useEffect(() => {
    try {
      sessionStorage.setItem('dashboardTicketVolume', ticketVolumePreset);
      sessionStorage.setItem('dashboardCustomStart', customStartDate);
      sessionStorage.setItem('dashboardCustomEnd', customEndDate);
    } catch (_) { /* ignore */ }
  }, [ticketVolumePreset, customStartDate, customEndDate]);

  useEffect(() => {
    if (user) {
      if (ticketVolumePreset === 'custom') {
        if (customStartDate && customEndDate) {
          fetchDashboardData({ startDate: customStartDate, endDate: customEndDate });
        } else {
          setLoading(false);
        }
      } else {
        fetchDashboardData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, ticketVolumePreset]);

  const fetchDashboardData = async (options = {}) => {
    const { silent = false, startDate, endDate } = options;

    // Use explicit args when provided, otherwise fall back to state
    const effectiveStart = startDate ?? customStartDate;
    const effectiveEnd   = endDate   ?? customEndDate;

    try {
      if (!silent) setLoading(true);
      
      let volParams = TICKET_VOLUME_PRESETS[ticketVolumePreset]?.api || TICKET_VOLUME_PRESETS['12'].api;
      
      if (ticketVolumePreset === 'custom') {
        if (!effectiveStart || !effectiveEnd) {
          toast.error('Please specify both a start and end boundary date.');
          if (!silent) setLoading(false);
          return false;
        }
        volParams = { range: 'custom', startDate: effectiveStart, endDate: effectiveEnd };
      } else if (ticketVolumePreset === '1w' || ticketVolumePreset === '2w') {
        volParams = { days: TICKET_VOLUME_PRESETS[ticketVolumePreset].api.days };
      }

      const response = isAdminScopeRole
        ? await getAdminDashboard(volParams)
        : await getCustomerDashboard(volParams);

      if (response && response.success) {
        const data = response.data || null;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[Dashboard] API Response:', {
            total: data?.total,
            byStatus: data?.byStatus,
            monthlyTickets: data?.monthlyTickets?.length || 0,
            byCompany: data?.byCompany?.length || 0,
            recentTickets: data?.recentTickets?.length || 0,
            preset: ticketVolumePreset,
            effectiveStart,
            effectiveEnd,
          });
        }
        
        setDashboardData(data);
        processDashboardData(data, ticketVolumePreset, effectiveStart, effectiveEnd);
        return true;
      }
      setDashboardData(null);
      processDashboardData(null, ticketVolumePreset, effectiveStart, effectiveEnd);
      return false;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      if (!silent) toast.error('Failed to load dashboard data');
      setDashboardData(null);
      const emptyMonthlyData = generateMonthlyData([], ticketVolumePreset, effectiveStart, effectiveEnd);
      setMonthlyData(emptyMonthlyData);
      setCustomerData([]);
      return false;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const processDashboardData = (data, presetKey, effectiveStart, effectiveEnd) => {
    const key = TICKET_VOLUME_PRESETS[presetKey] ? presetKey : '12';
    const start = effectiveStart ?? customStartDate;
    const end   = effectiveEnd   ?? customEndDate;

    if (!data) {
      setMonthlyData(generateMonthlyData([], key, start, end));
      setCustomerData([]);
      return;
    }

    if (data.monthlyTickets && Array.isArray(data.monthlyTickets)) {
      const processedMonthly = processMonthlyDataFromAPI(data.monthlyTickets, key, start, end);
      const processedTotal   = processedMonthly.reduce((sum, item) => sum + (Number(item?.tickets) || 0), 0);
      
      if (processedTotal === 0 && Number(data.total || 0) > 0 && Array.isArray(data.recentTickets) && data.recentTickets.length > 0) {
        setMonthlyData(generateMonthlyData(data.recentTickets, key, start, end));
      } else {
        setMonthlyData(processedMonthly);
      }
    } else {
      setMonthlyData(generateMonthlyData(data.recentTickets || [], key, start, end));
    }

    if (isAdminScopeRole && data.byCompany && Array.isArray(data.byCompany)) {
      const companies = data.byCompany
        .filter(item => item && (item.count > 0 || item.count === 0))
        .map(item => ({
          company_name: item.company_name || 'Unknown Company',
          count: item.count || 0,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);
      setCustomerData(companies);
    } else {
      setCustomerData([]);
    }
  };

  const getEffectiveDates = () => {
    const cfg = TICKET_VOLUME_PRESETS[ticketVolumePreset] || TICKET_VOLUME_PRESETS['12'];
    const now = new Date();
    let from = '';
    let to = formatDateString(now);

    if (cfg.mode === 'days') {
      const d = new Date();
      d.setDate(now.getDate() - (cfg.api.days - 1));
      from = formatDateString(d);
    } else if (cfg.mode === 'custom' && customStartDate && customEndDate) {
      from = customStartDate;
      to = customEndDate;
    } else if (cfg.mode === 'rolling') {
      const d = new Date();
      d.setMonth(now.getMonth() - (cfg.api.months || 12));
      from = formatDateString(d);
    } else if (cfg.mode === 'ytd') {
      from = `${now.getFullYear()}-01-01`;
    }
    return { from, to };
  };

  const handleMetricClick = (metricType) => {
    const userRole  = normalizedRole;
    const basePath  = isAdminScopeRole ? '/admin/tickets' : '/customer/tickets';
    const { from, to } = getEffectiveDates();
    
    let url = basePath;
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    
    switch (metricType) {
      case 'total':
        // use basePath
        break;
      case 'resolved':
        params.append('filter', 'resolved');
        params.append('status', 'resolved,closed');
        break;
      case 'pending': {
        if (userRole === 'admin' || userRole === 'employee' || userRole === 'super_admin' || userRole === 'global_support') {
          url = '/admin/tickets/pending';
        } else {
          url = '/customer/tickets/open';
        }
        break;
      }
      default:
        break;
    }
    
    const queryString = params.toString();
    navigate(queryString ? `${url}?${queryString}` : url);
  };

  const getMetrics = () => {
    if (!dashboardData) {
      return {
        total: 0,
        resolved: 0,
        pending: 0,
        resolutionRate: 0,
        resolvedOnly: 0,
        closedOnly: 0,
      };
    }
  
    const total = Number(dashboardData.total || 0);
  
    const byStatus = dashboardData.byStatus || {};
  
    // normalize statuses
    const normalizedStatus = {};
  
    Object.entries(byStatus).forEach(([key, value]) => {
      normalizedStatus[String(key).trim().toLowerCase()] = Number(value || 0);
    });
  
    const resolvedOnly =
      normalizedStatus.resolved || 0;
  
    const closedOnly =
      normalizedStatus.closed || 0;
  
    const resolved = resolvedOnly + closedOnly;
  
    const openCount =
      normalizedStatus.open || 0;
  
    const inProgressCount =
      normalizedStatus.in_progress ||
      normalizedStatus['in-progress'] ||
      normalizedStatus.inprogress ||
      0;
  
    const pending = openCount + inProgressCount;
  
    const resolutionRate =
      total > 0
        ? ((resolved / total) * 100).toFixed(1)
        : 0;
  
    return {
      total,
      resolved,
      pending,
      resolutionRate,
      resolvedOnly,
      closedOnly,
    };
  };

  const metrics = getMetrics();

  const escapeCsv = (cell) => {
    const s = cell == null ? '' : String(cell);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleRefresh = async () => {
    const ok = await fetchDashboardData({ silent: true });
    if (ok) toast.success('Dashboard refreshed');
    else toast.error('Could not refresh dashboard');
  };

  const handleCreateTicket = () => {
    navigate('/tickets/create');
  };

  const handleExport = () => {
    try {
      const currentLabel = ticketVolumePreset === 'custom'
        ? `Custom Range (${customStartDate} to ${customEndDate})`
        : (TICKET_VOLUME_PRESETS[ticketVolumePreset]?.label || 'period');

      const rows = [
        ['Ticket dashboard export', new Date().toISOString()],
        [],
        ['Metric', 'Value'],
        ['Total tickets', metrics.total],
        ['Resolved (incl. closed)', metrics.resolved],
        ['Pending (open + in progress)', metrics.pending],
        ['Resolution rate %', metrics.resolutionRate],
        [],
        [`Timeline (${currentLabel})`, 'Tickets created'],
        ...(Array.isArray(monthlyData) ? monthlyData.map((d) => [d.month, d.tickets]) : []),
      ];
      if (dashboardData?.recentTickets?.length) {
        rows.push([], ['Recent tickets', 'Number', 'Subject', 'Status']);
        dashboardData.recentTickets.slice(0, 20).forEach((t) => {
          rows.push([
            '',
            t.ticket_number || t.id,
            (t.subject || '').slice(0, 120),
            t.status || '',
          ]);
        });
      }
      const csv  = rows.map((r) => r.map(escapeCsv).join(',')).join('\r\n');
      const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
      a.rel      = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Dashboard exported');
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    }
  };

  const LoadingSkeleton = () => (
    <DashboardLayout userRole={user?.role}>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="w-full max-w-none">
          <div className="mb-8 sm:mb-10">
            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-gray-200/50 dark:border-gray-700/50 shadow-lg">
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-3 animate-pulse"></div>
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-8 sm:mb-10">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl border-l-4 border-gray-300 dark:border-gray-600 p-5 sm:p-6 shadow-md animate-pulse">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-4"></div>
                <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );

  if (loading) {
    return <LoadingSkeleton />;
  }

  let computedDateLabel = TICKET_VOLUME_PRESETS[ticketVolumePreset]?.label || 'Last 12 months';
  if (ticketVolumePreset === 'custom' && customStartDate && customEndDate) {
    computedDateLabel = `${customStartDate} to ${customEndDate}`;
  }

  return (
    <DashboardLayout userRole={user?.role}>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="w-full max-w-none">
          <DashboardHeader
            onRefresh={handleRefresh}
            onExport={handleExport}
            onCreate={handleCreateTicket}
            ticketVolumePreset={ticketVolumePreset}
            onTicketVolumeChange={setTicketVolumePreset}
            volumePresets={TICKET_VOLUME_PRESETS}
            dateRangeLabel={computedDateLabel}
          />
          
          {ticketVolumePreset === 'custom' && (
            <div className="mb-6 p-4 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-gray-200/60 dark:border-slate-700/60 rounded-xl shadow-sm flex flex-wrap items-end gap-4 transition-all duration-200 animate-fadeIn">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="button"
                disabled={!customStartDate || !customEndDate}
                onClick={() => fetchDashboardData({ startDate: customStartDate, endDate: customEndDate })}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 dark:disabled:bg-slate-700 disabled:text-gray-400 dark:disabled:text-slate-500 text-white text-sm font-medium rounded-lg shadow-sm transition-colors cursor-pointer disabled:cursor-not-allowed h-[36px]"
              >
                Apply Range
              </button>

              {(!customStartDate || !customEndDate) && (
                <span className="text-xs text-amber-500 font-medium pb-2">
                  * Please choose a target timeline boundary to continue.
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-8 sm:mb-10">
            <MetricCard
              title="Total Tickets"
              value={metrics.total.toLocaleString()}
              change={`Tickets in ${computedDateLabel}`}
              changeType="neutral"
              icon={Ticket}
              delay={0}
              clickable={true}
              onClick={() => handleMetricClick('total')}
            />
            <MetricCard
              title="Resolved"
              value={metrics.resolved.toLocaleString()}
              change={`${metrics.resolvedOnly} Resolved | ${metrics.closedOnly} Closed`}
              changeType="positive"
              icon={CheckCircle}
              delay={50}
              clickable={true}
              onClick={() => handleMetricClick('resolved')}
            />
            <MetricCard
              title="Pending"
              value={metrics.pending.toLocaleString()}
              change={`${metrics.total > 0 ? ((metrics.pending / metrics.total) * 100).toFixed(1) : 0}% of scope`}
              changeType="neutral"
              icon={Clock}
              delay={100}
              clickable={true}
              onClick={() => handleMetricClick('pending')}
            />
          </div>

          {normalizedRole === 'super_admin' ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-full">
                  <MonthlyTicketsChart
                    data={Array.isArray(monthlyData) ? monthlyData : []}
                    periodLabel={computedDateLabel}
                  />
                </div>
                <div className="h-full">
                  <CustomerTicketsTable
                    customers={Array.isArray(customerData) ? customerData : []}
                    title="Tickets by Company"
                  />
                </div>
              </div>
              <div className="w-full">
                <RecentTickets 
                  tickets={Array.isArray(dashboardData?.recentTickets) ? dashboardData.recentTickets : []} 
                  onCreate={handleCreateTicket}
                />
              </div>
            </div>
          ) : normalizedRole === 'customer' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-stretch mb-8 sm:mb-10">
              <div className="min-h-[280px] lg:min-h-[320px] h-full">
                <MonthlyTicketsChart
                  data={Array.isArray(monthlyData) ? monthlyData : []}
                  periodLabel={computedDateLabel}
                />
              </div>
              <div className="min-h-[280px] lg:min-h-[320px] h-full">
                <RecentTickets 
                  tickets={Array.isArray(dashboardData?.recentTickets) ? dashboardData.recentTickets : []} 
                  onCreate={handleCreateTicket}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8 sm:mb-10">
                <MonthlyTicketsChart
                  data={Array.isArray(monthlyData) ? monthlyData : []}
                  periodLabel={computedDateLabel}
                />
              </div>
              <div className="mb-8 sm:mb-10">
                <RecentTickets 
                  tickets={Array.isArray(dashboardData?.recentTickets) ? dashboardData.recentTickets : []} 
                  onCreate={handleCreateTicket}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
